import { popLayers, staleLayerEntry } from "./history_stack";
import { CHANNELS } from "../shared/channels";
import { WebTab } from "../shared/types";
import { isWeb, listen } from "./bridge";
import { byId, flashToast } from "./dom";
import { iconSvg } from "./icons";
import { buzz } from "./tweens";
import { rotary, Rotary } from "./dials";
import { createTuner, Tuner } from "./radio";
import { canUse } from "./me";
import { getPins, isPinned, loadPins, MAX_PINS, onPinsChanged, togglePin } from "./pins";
import { t } from "./i18n";
import { defaultDetail, KNOWN_VIEWS, openTarget, PLACES, Place, placeById, placeForView, RENAMED_VIEWS, SETUP_PLACE, Station, STATIONS, stationForPlace } from "./nav_map";

// chi's radio. the rail (desktop) has search, the mode knob and a fold for every station, the phone dock has the menu,
// the tuner and chi. pinned places sit above the dock and at the top of the rail as numbered presets. the bar over each
// page switches between the places of a station, and the menu lists every place once, with search and recents

type ShowHook = (detail: string) => void;

const VIEW_KEY = "chix-view";
const LAST_KEY = "chix-station-last";
const RECENT_KEY = "chix-recent-places";
const RECENT_MAX = 5;
const showHooks = new Map<string, ShowHook>();
const hideHooks = new Map<string, () => void>();
let currentView = "";
let currentDetail = "";
let webTabs: WebTab[] = [];
let tuner: Tuner | null = null;
let knob: Rotary | null = null;
let titlePin: HTMLButtonElement | null = null;
let titleWatch: MutationObserver | null = null;
let currentStation: number | null = null;
// stations folded open in the rail. the current one always opens when you arrive in it
const openFolds = new Set<number>();
let launcherOpener: HTMLElement | null = null;
let tuneTimer = 0;

export function onViewShown(viewName: string, hook: ShowHook): void {
  showHooks.set(viewName, hook);
}

export function onViewHidden(viewName: string, hook: () => void): void {
  hideHooks.set(viewName, hook);
}

export function isViewActive(viewName: string): boolean {
  return currentView === viewName;
}

function visiblePlaces(): Place[] {
  return PLACES.filter((place) => !(place.desk && isWeb()));
}

interface KeySpec {
  id: string;
  label: string;
  title: string;
  icon: string;
  href?: string;
  tab?: string;
  letter?: string;
  words?: string;
}

function placeKey(place: Place): KeySpec {
  return { id: place.id, label: place.label, title: place.title, icon: place.icon, words: place.words };
}

function tabKey(tab: WebTab): KeySpec {
  return {
    id: `web:${tab.id}`,
    label: tab.name.toLowerCase().slice(0, 12),
    title: tab.name,
    icon: "web",
    letter: tab.name.slice(0, 1).toUpperCase(),
    tab: tab.id,
    href: isWeb() ? tab.url : undefined
  };
}

export function buildKey(spec: KeySpec, kind: "rail" | "dock" | "deck" | "bar"): HTMLElement {
  const key = document.createElement(spec.href ? "a" : "button");
  key.className = `key key-${kind}`;
  key.title = spec.title;
  key.dataset.place = spec.id;
  if (spec.tab) key.dataset.tab = spec.tab;
  if (spec.words) key.dataset.words = spec.words;
  if (spec.href) {
    const link = key as HTMLAnchorElement;
    link.href = spec.href;
    link.target = "_blank";
    link.rel = "noopener";
  } else {
    (key as HTMLButtonElement).type = "button";
  }
  const cap = document.createElement("span");
  cap.className = "key-cap";
  cap.innerHTML = spec.letter ? `<span class="key-letter">${spec.letter}</span>` : iconSvg(spec.icon, kind === "deck" ? 26 : 18);
  const label = document.createElement("span");
  label.className = "key-label";
  label.textContent = kind === "deck" || kind === "rail" || kind === "bar" ? spec.title : spec.label;
  key.append(cap, label);
  return key;
}

// a pin id is a place id or web:<tab id>. one that points at nothing right now (a tab not loaded yet, a place not shared) just isn't shown
function pinSpec(id: string): KeySpec | null {
  if (id.startsWith("web:")) {
    const tab = webTabs.find((item) => item.id === id.slice(4));
    return tab ? tabKey(tab) : null;
  }
  const place = placeById(id);
  if (!place || (place !== SETUP_PLACE && !visiblePlaces().includes(place))) return null;
  return placeKey(place);
}

function pinnedSpecs(): KeySpec[] {
  return getPins().flatMap((id) => {
    const spec = pinSpec(id);
    return spec ? [spec] : [];
  });
}

function syncPin(button: HTMLElement, name: string): void {
  const pinned = isPinned(button.dataset.pin || "");
  button.setAttribute("aria-pressed", String(pinned));
  button.setAttribute("aria-label", t(pinned ? "Unpin {name}" : "Pin {name}", { name }));
}

function pinButton(id: string, name: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "pin";
  button.dataset.pin = id;
  button.dataset.name = name;
  button.innerHTML = iconSvg("star", 20);
  syncPin(button, name);
  return button;
}

function pressPin(button: HTMLElement): void {
  buzz();
  if (!togglePin(button.dataset.pin || "")) flashToast(t("all {count} presets are taken, unpin one first", { count: MAX_PINS }));
}

function presetKey(spec: KeySpec, slot: number): HTMLButtonElement {
  const preset = document.createElement("button");
  preset.type = "button";
  preset.className = "preset";
  preset.title = spec.title;
  preset.dataset.place = spec.id;
  if (spec.tab) preset.dataset.tab = spec.tab;
  const number = document.createElement("small");
  number.textContent = `P${slot}`;
  const label = document.createElement("span");
  label.textContent = spec.label;
  preset.append(number, label);
  return preset;
}

// the places of a station as keys, web tabs stand in for the library's "web" slot
function stationKeys(station: Station, kind: "rail" | "deck" | "bar"): HTMLElement[] {
  const allowed = new Set(visiblePlaces().map((place) => place.id));
  return station.places.flatMap((id) => {
    if (id === "web") return webTabs.map((tab) => buildKey(tabKey(tab), kind));
    if (id === SETUP_PLACE.id) return [buildKey(placeKey(SETUP_PLACE), kind)];
    const place = placeById(id);
    return place && allowed.has(id) ? [buildKey(placeKey(place), kind)] : [];
  });
}

function stationOfView(viewName: string, detail: string): number | null {
  if (viewName === "web") return STATIONS.findIndex((station) => station.id === "tools");
  const place = placeForView(viewName, detail);
  if (!place) return null;
  const station = stationForPlace(place.id);
  return station ? STATIONS.indexOf(station) : null;
}

function railPresets(): HTMLElement | null {
  const specs = pinnedSpecs();
  if (!specs.length) return null;
  const wrap = document.createElement("div");
  wrap.className = "rail-presets";
  const caption = document.createElement("span");
  caption.className = "bank-label";
  caption.textContent = `${t("presets")} · ${specs.length} / ${MAX_PINS}`;
  const list = document.createElement("div");
  list.className = "presets";
  list.append(...specs.map((spec, index) => presetKey(spec, index + 1)));
  wrap.append(caption, list);
  return wrap;
}

function paintPresets(): void {
  const strip = byId("presets");
  const specs = pinnedSpecs();
  strip.hidden = !specs.length;
  document.body.classList.toggle("has-presets", specs.length > 0);
  const slots = specs.map((spec, index) => presetKey(spec, index + 1));
  for (let at = specs.length; at < MAX_PINS; at++) {
    const free = document.createElement("div");
    free.className = "preset empty";
    free.setAttribute("aria-hidden", "true");
    free.innerHTML = `<small>P${at + 1}</small>`;
    slots.push(free as unknown as HTMLButtonElement);
  }
  strip.replaceChildren(...slots);
  const old = document.querySelector(".rail-presets");
  const next = railPresets();
  if (old && next) old.replaceWith(next);
  else if (old) old.remove();
  else if (next) document.querySelector(".knob-ring")?.after(next);
  document.querySelectorAll<HTMLElement>(".pin[data-pin]").forEach((button) => syncPin(button, button.dataset.name || ""));
  paintPinnedBank();
  markKeys(currentView, currentDetail);
}

function paintRail(): void {
  const rail = byId("rail");
  const brand = document.createElement("div");
  brand.className = "rail-brand";
  brand.innerHTML = `<span class="chi-mark" aria-hidden="true"></span><span>CHIX</span>`;

  const find = document.createElement("button");
  find.type = "button";
  find.className = "rail-find";
  find.setAttribute("aria-haspopup", "dialog");
  find.innerHTML = `${iconSvg("search", 16)}<span>${t("go to…")}</span><kbd>Ctrl K</kbd>`;
  find.addEventListener("click", () => openLauncher(true));

  const knobWrap = document.createElement("div");
  knobWrap.className = "knob-ring";
  const knobEl = document.createElement("div");
  knobEl.className = "knob";
  knobEl.setAttribute("aria-label", t("mode"));
  knobEl.innerHTML = `<span class="knob-skirt"></span><span class="knob-cap"></span>`;
  knobWrap.append(knobEl);
  knob = rotary(knobEl, STATIONS.length, (index) => tuneTo(index), currentStation ?? 0);

  if (currentStation !== null) openFolds.add(currentStation);
  const menu = document.createElement("ul");
  menu.className = "station-menu";
  STATIONS.forEach((station, index) => {
    const item = document.createElement("li");
    const open = openFolds.has(index);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "station";
    button.dataset.station = String(index);
    button.setAttribute("aria-expanded", String(open));
    button.setAttribute("aria-controls", `station-places-${station.id}`);
    button.innerHTML = `<i class="station-icon" aria-hidden="true">${iconSvg(station.icon, 18)}</i><span></span><em>${station.freq}</em><b class="station-fold" aria-hidden="true"></b>`;
    button.querySelector("span")!.textContent = station.label;
    // a station header folds its places open or shut, picking a place is what changes the page
    button.addEventListener("click", () => {
      buzz();
      const now = !openFolds.has(index);
      if (now) openFolds.add(index);
      else openFolds.delete(index);
      button.setAttribute("aria-expanded", String(now));
      const sub = document.getElementById(`station-places-${station.id}`);
      if (sub) sub.hidden = !now;
    });
    item.append(button);
    const sub = document.createElement("div");
    sub.className = "station-places";
    sub.id = `station-places-${station.id}`;
    sub.hidden = !open;
    sub.append(...stationKeys(station, "rail"));
    item.append(sub);
    menu.append(item);
  });

  const spacer = document.createElement("div");
  spacer.className = "rail-spacer";
  const chi = buildKey({ id: "chat", label: "chi", title: t("Talk to Chi"), icon: "ask" }, "rail");
  chi.classList.add("key-chi");
  const presets = railPresets();
  rail.replaceChildren(brand, find, knobWrap, ...(presets ? [presets] : []), menu, spacer, ...(canUse("chi") ? [chi] : []));
}

function paintDock(): void {
  const dock = byId("dock");
  const menu = buildKey({ id: "deck", label: t("menu"), title: t("Menu"), icon: "grid" }, "dock");
  menu.classList.add("key-deck-toggle");
  menu.setAttribute("aria-haspopup", "dialog");
  const chi = buildKey({ id: "chat", label: "chi", title: t("Talk to Chi"), icon: "ask" }, "dock");
  chi.classList.add("key-chi");
  chi.querySelector(".key-cap")!.innerHTML = `<span class="chi-mark" aria-hidden="true"></span>`;
  const host = document.createElement("div");
  host.setAttribute("role", "group");
  host.setAttribute("aria-label", t("station tuner. tap for all stations, drag to tune, arrow keys to step"));
  host.tabIndex = 0;
  tuner = createTuner(host, STATIONS, (index) => tuneTo(index), () => openLauncher(true));
  tuner.show(currentStation);
  dock.replaceChildren(menu, host, ...(canUse("chi") ? [chi] : []));
}

// a deck key with its pin in the corner. the pin is a sibling because a button can't hold a button
function withPin(key: HTMLElement): HTMLElement {
  const id = key.dataset.place;
  if (!id) return key;
  const slot = document.createElement("div");
  slot.className = "deck-slot";
  slot.append(key, pinButton(id, key.title));
  return slot;
}

function pinnedBank(): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "bank bank-deck bank-pinned";
  const caption = document.createElement("span");
  caption.className = "bank-label";
  const specs = pinnedSpecs();
  caption.textContent = `${t("pinned")} · ${specs.length} / ${MAX_PINS}`;
  const items = document.createElement("div");
  items.className = "bank-keys";
  if (specs.length) items.append(...specs.map((spec) => withPin(buildKey(spec, "deck"))));
  else {
    const note = document.createElement("p");
    note.className = "bank-note";
    note.textContent = t("tap the star on a key to pin it. pins sit above the dock as presets");
    items.append(note);
  }
  wrap.append(caption, items);
  return wrap;
}

function paintPinnedBank(): void {
  const old = document.querySelector(".bank-pinned");
  if (!old) return;
  old.replaceWith(pinnedBank());
  filterLauncher();
}

// the station sheet at the top of the menu: all stations as big tiles, so the map is never hidden behind a dial
function stationTiles(): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "bank bank-stations";
  const caption = document.createElement("span");
  caption.className = "bank-label";
  caption.textContent = t("stations");
  const grid = document.createElement("div");
  grid.className = "station-tiles";
  STATIONS.forEach((station, index) => {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "station-tile";
    tile.dataset.station = String(index);
    tile.setAttribute("aria-current", String(index === currentStation));
    tile.innerHTML = `<i aria-hidden="true">${iconSvg(station.icon, 22)}</i><b></b><small></small><em>${station.freq}</em>`;
    tile.querySelector("b")!.textContent = station.label;
    tile.querySelector("small")!.textContent = station.blurb;
    tile.addEventListener("click", () => tuneTo(index));
    grid.append(tile);
  });
  wrap.append(caption, grid);
  return wrap;
}

function recentBank(): HTMLElement | null {
  const here = currentPlaceId();
  const specs = recentIds().filter((id) => id !== here).flatMap((id) => {
    const spec = pinSpec(id);
    return spec ? [spec] : [];
  }).slice(0, RECENT_MAX);
  if (!specs.length) return null;
  const wrap = document.createElement("section");
  wrap.className = "bank bank-deck bank-recent";
  const caption = document.createElement("span");
  caption.className = "bank-label";
  caption.textContent = t("recent");
  const items = document.createElement("div");
  items.className = "bank-keys";
  items.append(...specs.map((spec) => buildKey(spec, "deck")));
  wrap.append(caption, items);
  return wrap;
}

function paintLauncher(): void {
  const banks: HTMLElement[] = [stationTiles()];
  const recent = recentBank();
  if (recent) banks.push(recent);
  banks.push(pinnedBank());
  for (const station of STATIONS) {
    const keys = stationKeys(station, "deck");
    if (!keys.length) continue;
    const wrap = document.createElement("section");
    wrap.className = "bank bank-deck bank-places";
    const caption = document.createElement("span");
    caption.className = "bank-label";
    caption.textContent = `${station.label} · ${station.freq}`;
    const items = document.createElement("div");
    items.className = "bank-keys";
    items.append(...keys.map(withPin));
    wrap.append(caption, items);
    banks.push(wrap);
  }
  byId("launcher-banks").replaceChildren(...banks);
  // every tile gets its turn in the light-up sequence
  let order = 0;
  byId("launcher-banks").querySelectorAll<HTMLElement>(".station-tile, .bank-keys > *").forEach((tile) => tile.style.setProperty("--i", String(order++)));
  filterLauncher();
}

function paintNav(): void {
  paintRail();
  paintDock();
  paintLauncher();
  paintPresets();
  markActive(currentView, currentDetail);
}

export function setWebTabs(tabs: WebTab[]): void {
  webTabs = tabs;
  paintNav();
}

// the old drag to reorder rail is gone, stations fix the order. kept so callers keep working
export async function applyNavOrder(_next?: string[]): Promise<void> {
  paintNav();
}

// the tools shelf, slides up from the dock, ctrl+k on desktop
function launcherOpen(): boolean {
  return document.body.classList.contains("deck-open");
}

export function openLauncher(open = !launcherOpen()): void {
  const was = launcherOpen();
  document.body.classList.toggle("deck-open", open);
  const sheet = byId("launcher");
  sheet.setAttribute("aria-hidden", String(!open));
  sheet.toggleAttribute("inert", !open);
  if (open && !was) {
    launcherOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    paintLauncher();
    const search = byId<HTMLInputElement>("launcher-search");
    search.value = "";
    filterLauncher();
    // a phone keeps the keyboard down until you tap search, the sheet is for browsing first
    if (matchMedia("(hover: hover)").matches) search.focus();
    else byId("launcher-close").focus({ preventScroll: true });
  } else if (!open && was) {
    const back = launcherOpener;
    launcherOpener = null;
    if (back && back.isConnected && !back.closest("[hidden]")) back.focus({ preventScroll: true });
  }
}

function filterLauncher(): void {
  const needle = (document.getElementById("launcher-search") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  document.querySelectorAll<HTMLElement>("#launcher-banks .key").forEach((key) => {
    key.hidden = Boolean(needle) && !`${key.title} ${key.textContent} ${key.dataset.words || ""}`.toLowerCase().includes(needle);
    if (key.parentElement?.classList.contains("deck-slot")) key.parentElement.hidden = key.hidden;
  });
  // while searching the station tiles and the recents step aside, results are the only thing on the shelf
  document.querySelectorAll<HTMLElement>("#launcher-banks .bank-stations, #launcher-banks .bank-recent").forEach((bank) => {
    bank.hidden = Boolean(needle);
  });
  document.querySelectorAll<HTMLElement>("#launcher-banks .bank-places, #launcher-banks .bank-pinned").forEach((bank) => {
    bank.hidden = ![...bank.querySelectorAll<HTMLElement>(".key")].some((key) => !key.hidden);
  });
  const status = document.getElementById("launcher-status");
  if (status) {
    const count = document.querySelectorAll("#launcher-banks .bank-places .key:not([hidden])").length;
    status.textContent = needle ? (count ? t("{count} places", { count }) : t("nothing matches that")) : "";
  }
}

// the last few places opened, newest first
function recentIds(): string[] {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]") as unknown;
    return Array.isArray(list) ? list.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function rememberRecent(id: string): void {
  if (!id) return;
  const next = [id, ...recentIds().filter((item) => item !== id)].slice(0, RECENT_MAX + 1);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // private mode, the recents just stay empty
  }
}

function currentPlaceId(): string {
  return currentView === "web" ? `web:${currentDetail}` : placeForView(currentView, currentDetail)?.id || "";
}

// each station remembers the place you last had open in it
function lastPlaces(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(LAST_KEY) || "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

function rememberPlace(stationIndex: number, placeId: string): void {
  const saved = lastPlaces();
  saved[STATIONS[stationIndex].id] = placeId;
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify(saved));
  } catch {
    // private mode, the station starts on its first place next time
  }
}

function tuneTo(index: number): void {
  const station = STATIONS[index];
  if (!station) return;
  buzz();
  const keys = stationKeys(station, "bar").filter((key) => !(key instanceof HTMLAnchorElement));
  const remembered = lastPlaces()[station.id];
  const pick = keys.find((key) => key.dataset.place === remembered) || keys[0];
  if (!pick) return;
  // body.tuning lasts a moment while the page changes station, the motion layer paints it
  document.body.classList.add("tuning");
  window.clearTimeout(tuneTimer);
  tuneTimer = window.setTimeout(() => document.body.classList.remove("tuning"), 450);
  openKey(pick);
}

function viewHash(viewName: string, detail: string): string {
  return detail ? `#${viewName}/${detail}` : `#${viewName}`;
}

function parseHash(): { view: string; detail: string } {
  const raw = location.hash.replace(/^#/, "").trim();
  if (!raw) return { view: "", detail: "" };
  const [first, ...rest] = raw.split("/");
  const view = RENAMED_VIEWS[first] || first;
  if (!KNOWN_VIEWS.includes(view)) return { view: "", detail: "" };
  return { view, detail: RENAMED_VIEWS[first] ? "" : rest.join("/") };
}

function rememberedView(): { view: string; detail: string } {
  const fromHash = parseHash();
  if (fromHash.view) return fromHash;
  try {
    const saved = JSON.parse(localStorage.getItem(VIEW_KEY) || "null") as { view?: string; detail?: string } | null;
    const view = saved?.view ? RENAMED_VIEWS[saved.view] || saved.view : "";
    if (view && KNOWN_VIEWS.includes(view)) return { view, detail: RENAMED_VIEWS[saved?.view || ""] ? "" : saved?.detail || "" };
  } catch {
    // ignore a broken stored tab
  }
  return { view: "today", detail: "" };
}

let booted = false;

function persistView(viewName: string, detail: string, push = false): void {
  try {
    localStorage.setItem(VIEW_KEY, JSON.stringify({ view: viewName, detail }));
  } catch {
    // private mode can refuse storage
  }
  const hash = viewHash(viewName, detail);
  const state = { view: viewName, detail };
  try {
    if (push) history.pushState(state, "", hash);
    else if (location.hash !== hash || history.state?.view !== viewName) history.replaceState(state, "", hash);
  } catch {
    // some embeds refuse history changes
  }
}

function placeTitle(viewName: string, detail: string): string {
  if (viewName === "web") return webTabs.find((tab) => tab.id === detail)?.name || t("web");
  return placeForView(viewName, detail)?.title || viewName;
}

// over each page: the places of its station (phones, the rail shows them on desktop), then the place's own panes
function paintPaneBar(viewName: string, detail: string): void {
  const bar = byId("pane-bar");
  const place = viewName === "web" ? null : placeForView(viewName, detail);
  const stationIndex = stationOfView(viewName, detail);
  const rows: HTMLElement[] = [];

  if (stationIndex !== null) {
    const keys = stationKeys(STATIONS[stationIndex], "bar");
    if (keys.length > 1) {
      const strip = document.createElement("div");
      strip.className = "station-bar";
      strip.setAttribute("role", "group");
      strip.setAttribute("aria-label", t("places in {name}", { name: STATIONS[stationIndex].label }));
      strip.append(...keys);
      rows.push(strip);
    }
  }

  const panes = place?.panes || [];
  if (panes.length > 1 && place) {
    const activeIndex = Math.max(0, panes.findIndex((pane) => (pane.view ? pane.view === viewName : (detail || panes[0].id) === pane.id)));
    const track = document.createElement("div");
    track.className = "switch";
    track.setAttribute("role", "tablist");
    track.setAttribute("aria-label", place.title);
    track.style.setProperty("--count", String(panes.length));
    track.style.setProperty("--at", String(activeIndex));
    const thumb = document.createElement("i");
    thumb.className = "switch-thumb";
    track.append(thumb, ...panes.map((pane, index) => {
      const option = document.createElement("button");
      option.type = "button";
      option.className = "switch-option";
      option.setAttribute("role", "tab");
      option.setAttribute("aria-selected", String(index === activeIndex));
      option.textContent = pane.label;
      option.classList.toggle("active", index === activeIndex);
      option.addEventListener("click", () => {
        if (index === activeIndex) return;
        buzz();
        const target = openTarget(place, pane.id);
        showView(target.view, target.detail);
      });
      return option;
    }));
    rows.push(track);
  }

  bar.hidden = !rows.length;
  document.body.classList.toggle("has-panes", panes.length > 1);
  document.body.classList.toggle("has-stations", rows.some((row) => row.classList.contains("station-bar")));
  bar.replaceChildren(...rows);
  markKeys(viewName, detail);
}

function markKeys(viewName: string, detail: string): void {
  const place = viewName === "web" ? null : placeForView(viewName, detail);
  document.querySelectorAll<HTMLElement>(".key[data-place]").forEach((key) => {
    const matches = viewName === "web" ? key.dataset.tab === detail : key.dataset.place === place?.id;
    key.classList.toggle("active", matches);
    if (matches) key.setAttribute("aria-current", "page");
    else key.removeAttribute("aria-current");
  });
  document.querySelectorAll<HTMLElement>(".preset[data-place]").forEach((preset) => {
    const matches = viewName === "web" ? preset.dataset.tab === detail : preset.dataset.place === place?.id;
    preset.setAttribute("aria-current", String(matches));
  });
  document.querySelectorAll<HTMLElement>(".key-deck-toggle").forEach((key) => key.classList.toggle("active", launcherOpen()));
  // keep the current place in view inside the scrolling bar, without scrolling the page itself
  document.querySelectorAll<HTMLElement>(".station-bar .key.active").forEach((key) => {
    const bar = key.parentElement;
    if (bar) bar.scrollLeft = key.offsetLeft - (bar.clientWidth - key.offsetWidth) / 2;
  });
}

function markActive(viewName: string, detail: string): void {
  const stationIndex = stationOfView(viewName, detail);
  const moved = stationIndex !== currentStation;
  currentStation = stationIndex;
  if (moved) paintRail();
  document.querySelectorAll<HTMLElement>(".station[data-station]").forEach((button) => {
    button.setAttribute("aria-current", String(Number(button.dataset.station) === stationIndex));
  });
  if (stationIndex !== null) knob?.set(stationIndex, false);
  if (stationIndex !== null) document.body.dataset.station = STATIONS[stationIndex].id;
  else delete document.body.dataset.station;
  document.querySelectorAll<HTMLElement>(".station-tile[data-station]").forEach((tile) => {
    tile.setAttribute("aria-current", String(Number(tile.dataset.station) === stationIndex));
  });
  tuner?.show(stationIndex, moved, viewName === "chat" ? "Chi" : t("Tool"));
  markKeys(viewName, detail);
}

// a one shot tear across the page title, like a signal catching
function glitch(title: HTMLElement): void {
  if (document.documentElement.classList.contains("calm") || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  title.classList.remove("glitch");
  void title.offsetWidth;
  title.classList.add("glitch");
}

// a small channel label over every page title
function stampCode(view: HTMLElement, viewName: string, detail: string): void {
  const stationIndex = stationOfView(viewName, detail);
  const station = stationIndex === null ? null : STATIONS[stationIndex];
  const code = station ? `CH 0${stationIndex! + 1} · ${station.freq} · ${station.label}` : viewName === "chat" ? t("Chi · direct line") : t("Tool");
  view.querySelectorAll<HTMLElement>(":scope h1, :scope .view-head h1, :scope .refs-bar h1").forEach((title) => {
    // a lightbox title is a character or picture name, not the page
    if (title.closest(".viewer")) return;
    title.dataset.code = code;
  });
}

export // a star after the page title pins or releases this place
function mountTitlePin(view: HTMLElement | null, viewName: string, detail: string): void {
  titleWatch?.disconnect();
  titlePin?.remove();
  titlePin = null;
  const id = viewName === "web" ? `web:${detail}` : placeForView(viewName, detail)?.id || "";
  const spec = id ? pinSpec(id) : null;
  const title = view && spec ? [...view.querySelectorAll<HTMLElement>("h1")].find((item) => !item.closest(".viewer") && item.offsetParent) : null;
  if (!title || !spec) return;
  const button = pinButton(id, spec.title);
  button.classList.add("pin-title");
  button.addEventListener("click", () => pressPin(button));
  title.append(button);
  titlePin = button;
  // some pages rewrite their title text, which would take the star with it
  titleWatch = new MutationObserver(() => {
    if (button.parentElement !== title) title.append(button);
  });
  titleWatch.observe(title, { childList: true });
}

// where a place sits on the whole dial, station by station. a move to a later place slides the page in from the right
function dialOrder(id: string): number {
  const key = id.startsWith("web:") ? "web" : id;
  let at = 0;
  for (const station of STATIONS) {
    const found = station.places.indexOf(key);
    if (found >= 0) return at + found;
    at += station.places.length;
  }
  return -1;
}

let dirTimer = 0;
function markDirection(fromId: string, toId: string): void {
  const from = dialOrder(fromId);
  const to = dialOrder(toId);
  if (from < 0 || to < 0 || from === to) {
    delete document.body.dataset.dir;
    return;
  }
  document.body.dataset.dir = to > from ? "next" : "prev";
  window.clearTimeout(dirTimer);
  dirTimer = window.setTimeout(() => delete document.body.dataset.dir, 700);
}

// the next or previous place inside the current station, for swipes. false when there is none that way
export function stepPlace(step: 1 | -1): boolean {
  if (currentStation === null) return false;
  const keys = stationKeys(STATIONS[currentStation], "bar").filter((key) => !(key instanceof HTMLAnchorElement));
  const here = currentPlaceId();
  const at = keys.findIndex((key) => (key.dataset.tab ? `web:${key.dataset.tab}` : key.dataset.place) === here);
  const next = keys[at + step];
  if (at < 0 || !next) return false;
  buzz();
  openKey(next);
  return true;
}

export function showView(viewName: string, detail = "", fromHash = false): void {
  const place = placeForView(viewName, detail);
  if (place?.desk && isWeb()) return showView("today");
  // a place that isn't shared with this person isn't in the map at all
  if (!place && viewName !== "web" && viewName !== "today") return showView("today");
  const resolved = place ? defaultDetail(place, detail) : detail;
  const changed = currentView !== viewName;
  if (currentView) markDirection(currentPlaceId(), viewName === "web" ? `web:${resolved}` : place?.id || "");
  const moved = changed || currentDetail !== resolved;
  if (changed && currentView) hideHooks.get(currentView)?.();
  const previous = currentView;
  currentView = viewName;
  currentDetail = resolved;
  openLauncher(false);

  document.querySelectorAll<HTMLElement>(".view").forEach((view) => {
    const on = view.id === `view-${viewName}`;
    view.classList.toggle("active", on);
    if (on) stampCode(view, viewName, resolved);
    if (on && changed && previous) {
      view.classList.remove("entering");
      void view.offsetWidth;
      view.classList.add("entering");
      const title = view.querySelector<HTMLElement>(".view-head h1, .hero h1, .refs-bar h1");
      if (title) glitch(title);
    }
  });

  const activeView = document.getElementById(`view-${viewName}`);
  if (activeView) {
    if (resolved) activeView.dataset.pane = resolved;
    else delete activeView.dataset.pane;
    document.body.dataset.view = viewName;
    if (changed) activeView.scrollTop = 0;
  }

  rememberRecent(viewName === "web" ? `web:${resolved}` : place?.id || "");
  const stationIndex = stationOfView(viewName, resolved);
  if (stationIndex !== null) rememberPlace(stationIndex, viewName === "web" ? `web:${resolved}` : place?.id || viewName);

  const title = placeTitle(viewName, resolved);
  const bar = document.getElementById("titlebar-place");
  if (bar) bar.textContent = title;
  document.title = `${title} · ChiX`;
  markActive(viewName, resolved);
  paintPaneBar(viewName, resolved);
  mountTitlePin(activeView, viewName, resolved);
  showHooks.get(viewName)?.(resolved);
  // a real move is a Back entry, a hash/popstate arrival or the boot view only syncs what is stored
  if (fromHash) persistView(viewName, resolved, false);
  else persistView(viewName, resolved, booted && moved);
}

export function restoreViewFromHash(): void {
  const target = rememberedView();
  showView(target.view, target.detail, true);
  persistView(target.view, target.detail);
  booted = true;
}

function openPlaceId(id: string): void {
  if (id.startsWith("web:")) {
    showView("web", id.slice(4));
    return;
  }
  const place = placeById(id);
  if (place) showView(place.view, place.detail || defaultDetail(place));
}

function openKey(key: HTMLElement): void {
  if (key.dataset.tab) {
    showView("web", key.dataset.tab);
    return;
  }
  const place = placeById(key.dataset.place || "");
  if (!place) return;
  showView(place.view, place.detail || defaultDetail(place));
}

function pressKey(key: HTMLElement): void {
  buzz();
  if (key.classList.contains("key-deck-toggle")) {
    openLauncher();
    markKeys(currentView, currentDetail);
    return;
  }
  openKey(key);
}

export function setupNavigation(): void {
  for (const id of ["rail", "dock", "presets", "launcher-banks", "pane-bar"]) {
    byId(id).addEventListener("click", (event) => {
      const pin = (event.target as Element).closest(".pin[data-pin]") as HTMLElement | null;
      if (pin) {
        pressPin(pin);
        return;
      }
      const key = (event.target as Element).closest(".key, .preset") as HTMLElement | null;
      if (!key || key instanceof HTMLAnchorElement) return;
      pressKey(key);
    });
  }
  byId("launcher-scrim").addEventListener("click", () => openLauncher(false));
  byId("launcher-close").addEventListener("click", () => openLauncher(false));
  byId("launcher-search").addEventListener("input", filterLauncher);
  byId("launcher-search").addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const first = document.querySelector<HTMLElement>("#launcher-banks .bank-places .key:not([hidden]), #launcher-banks .key:not([hidden])");
    if (first) first.click();
  });
  window.addEventListener("keydown", (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      openLauncher();
    } else if (event.altKey && !event.ctrlKey && !event.metaKey && /^Digit[1-6]$/.test(event.code)) {
      const spec = pinnedSpecs()[Number(event.code.slice(5)) - 1];
      if (!spec) return;
      event.preventDefault();
      buzz();
      openPlaceId(spec.id);
    } else if (event.key === "Escape" && launcherOpen()) {
      openLauncher(false);
    } else if (launcherOpen() && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      const keys = [...document.querySelectorAll<HTMLElement>("#launcher-banks .key:not([hidden]), #launcher-banks .station-tile")].filter((key) => key.offsetParent);
      if (!keys.length) return;
      const at = keys.indexOf(document.activeElement as HTMLElement);
      const next = event.key === "ArrowDown" ? Math.min(keys.length - 1, at + 1) : Math.max(0, at - 1);
      event.preventDefault();
      keys[next].focus();
    } else if (event.key === "Tab" && launcherOpen()) {
      // the menu is a modal sheet, tab cycles inside it
      const items = [...byId("launcher").querySelectorAll<HTMLElement>("button, input, a[href]")].filter((item) => !item.closest("[hidden]") && item.offsetParent);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
  paintNav();
  onPinsChanged(paintPresets);
  void loadPins();
  listen<{ view: string; detail: string }>(CHANNELS.navigate, (target) => showView(target.view, target.detail));
  window.addEventListener("popstate", () => {
    if (popLayers() || staleLayerEntry()) return;
    const state = history.state as { view?: string; detail?: string } | null;
    const target = state?.view && KNOWN_VIEWS.includes(state.view) ? { view: state.view, detail: state.detail || "" } : parseHash();
    if (target.view && (target.view !== currentView || target.detail !== currentDetail)) showView(target.view, target.detail, true);
  });
  window.addEventListener("hashchange", () => {
    const target = parseHash();
    if (target.view && (target.view !== currentView || target.detail !== currentDetail)) showView(target.view, target.detail, true);
  });
}
